// This file is auto-generated. Do not edit manually.
import { useEffect, useState } from 'react';

import { CSS_VARIABLE_MAP } from './types';

import type { CSSVariableCamelCase, CSSVariableName } from './types';

/**
 * Hook for getting CSS variable value
 * @param variableName - Variable name in camelCase or kebab-case format
 * @param element - Element to get variable from (defaults to document.documentElement)
 * @returns CSS variable value
 *
 * @example
 * const accentColor = useCSSVariable('colorAccent')
 * const moduleSize = useCSSVariable('module-2') // kebab-case also supported
 */
export const useCSSVariable = (
  variableName: CSSVariableCamelCase | CSSVariableName,
  element: HTMLElement = document.documentElement,
): string => {
  const [value, setValue] = useState<string>(() => {
    const cssVarName =
      variableName in CSS_VARIABLE_MAP ? CSS_VARIABLE_MAP[variableName as CSSVariableCamelCase] : variableName;

    return getComputedStyle(element).getPropertyValue(`--${cssVarName}`).trim();
  });

  useEffect(() => {
    const cssVarName =
      variableName in CSS_VARIABLE_MAP ? CSS_VARIABLE_MAP[variableName as CSSVariableCamelCase] : variableName;

    const observer = new MutationObserver(() => {
      const newValue = getComputedStyle(element).getPropertyValue(`--${cssVarName}`).trim();

      setValue((prevValue) => {
        if (newValue !== prevValue) {
          return newValue;
        }
        return prevValue;
      });
    });

    observer.observe(element, {
      attributeFilter: ['style', 'class'],
      attributes: true,
    });

    return () => observer.disconnect();
  }, [variableName, element]);

  return value;
};

/**
 * Get CSS variable value (non-reactive)
 * @param variableName - Variable name in camelCase or kebab-case format
 * @param element - Element to get variable from (defaults to document.documentElement)
 * @returns CSS variable value
 *
 * @example
 * const accentColor = getCSSVariable('colorAccent')
 */
export const getCSSVariable = (
  variableName: CSSVariableCamelCase | CSSVariableName,
  element: HTMLElement = document.documentElement,
): string => {
  const cssVarName =
    variableName in CSS_VARIABLE_MAP ? CSS_VARIABLE_MAP[variableName as CSSVariableCamelCase] : variableName;

  return getComputedStyle(element).getPropertyValue(`--${cssVarName}`).trim();
};

/**
 * Get computed CSS variable value (resolves calc(), var(), etc.)
 * @param variableName - Variable name in camelCase or kebab-case format
 * @param element - Element to get variable from (defaults to document.documentElement)
 * @returns Computed CSS variable value
 *
 * @example
 * // If --module-2: calc(var(--module) * 2), where --module: 7px
 * const moduleSize = getComputedCSSVariable('module2') // '14px'
 */
export const getComputedCSSVariable = (
  variableName: CSSVariableCamelCase | CSSVariableName,
  element: HTMLElement = document.documentElement,
): string => {
  const cssVarName =
    variableName in CSS_VARIABLE_MAP ? CSS_VARIABLE_MAP[variableName as CSSVariableCamelCase] : variableName;

  const tempElement = document.createElement('div');
  tempElement.style.position = 'absolute';
  tempElement.style.visibility = 'hidden';
  tempElement.style.width = `var(--${cssVarName})`;
  element.appendChild(tempElement);

  const computedValue = getComputedStyle(tempElement).width;
  element.removeChild(tempElement);

  if (computedValue === 'auto' || computedValue === '0px') {
    tempElement.style.width = 'auto';
    tempElement.style.color = `var(--${cssVarName})`;
    element.appendChild(tempElement);

    const colorValue = getComputedStyle(tempElement).color;
    element.removeChild(tempElement);

    if (colorValue !== 'rgb(0, 0, 0)' || getCSSVariable(variableName, element).includes('#')) {
      return colorValue;
    }
  }

  return computedValue;
};

/**
 * React hook to get computed CSS variable value with reactivity
 * @param variableName - Variable name in camelCase or kebab-case format
 * @param element - Element to get variable from (defaults to document.documentElement)
 * @returns Computed CSS variable value
 *
 * @example
 * // If --module-2: calc(var(--module) * 2), where --module: 7px
 * const moduleSize = useComputedCSSVariable('module2') // '14px'
 */
export const useComputedCSSVariable = (
  variableName: CSSVariableCamelCase | CSSVariableName,
  element: HTMLElement = document.documentElement,
): string => {
  const [value, setValue] = useState<string>(() => getComputedCSSVariable(variableName, element));

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const newValue = getComputedCSSVariable(variableName, element);

      setValue((prevValue) => {
        if (newValue !== prevValue) {
          return newValue;
        }
        return prevValue;
      });
    });

    observer.observe(element, {
      attributeFilter: ['style', 'class'],
      attributes: true,
    });

    return () => observer.disconnect();
  }, [variableName, element]);

  return value;
};

/**
 * Set CSS variable value
 * @param variableName - Variable name in camelCase or kebab-case format
 * @param value - Value to set
 * @param element - Element to set variable on (defaults to document.documentElement)
 *
 * @example
 * setCSSVariable('moduleColor', '#ff0000')
 */
export const setCSSVariable = (
  variableName: CSSVariableCamelCase | CSSVariableName,
  value: string,
  element: HTMLElement = document.documentElement,
): void => {
  const cssVarName =
    variableName in CSS_VARIABLE_MAP ? CSS_VARIABLE_MAP[variableName as CSSVariableCamelCase] : variableName;

  element.style.setProperty(`--${cssVarName}`, value);
};
